import * as XLSX from 'xlsx';
import { validateCountry } from '../services/geoService';

export const parseRouteExcel = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);

        if (!data || data.length === 0) throw new Error("Excel vacío o formato incorrecto");

        const routesMap = {};
        let lastRouteName = null;
        let bounds = { minLat: 90, maxLat: -90, minLon: 180, maxLon: -180, hasValidCoords: false };

        const globalCoordWeights = {}; 

        for (let i = 0; i < data.length; i++) {
            const row = data[i];
            const excelRowNumber = i + 2; // +2 porque la fila 1 son los encabezados en Excel

            // 1. VALIDAR NOMBRES DE RUTAS DUPLICADOS
            const currentRouteNameRaw = row["Nombre Ruta"];
            let currentRouteName = currentRouteNameRaw ? String(currentRouteNameRaw).trim() : null;

            if (currentRouteName) {
                if (currentRouteName !== lastRouteName) {
                    // Si el nombre es distinto al anterior, pero ya existe en el mapa de rutas
                    if (routesMap[currentRouteName]) {
                        throw new Error(`Fila ${excelRowNumber}: El nombre de ruta "${currentRouteName}" está duplicado en el archivo. Cada ruta debe tener un nombre único.`);
                    }
                } else {
                    // Si el nombre es IGUAL al anterior, pero la fila de arriba estaba vacía en la columna "Nombre Ruta"
                    // Significa que es un bloque separado con el mismo nombre (Como tu caso con Ruta A)
                    if (i > 0 && !data[i - 1]["Nombre Ruta"]) {
                        throw new Error(`Fila ${excelRowNumber}: El nombre de ruta "${currentRouteName}" está duplicado (declarado en bloques separados). Agrupe todas las paradas en un solo bloque o use un nombre distinto.`);
                    }
                }
                lastRouteName = currentRouteName;
            }
            
            if (!lastRouteName) continue;

            let latRaw = row["Latitud"];
            let lonRaw = row["Longitud"];
            let pesoRaw = row["Peso"];

            let pesoLimpio = 0;
            if (pesoRaw !== undefined && pesoRaw !== null && pesoRaw !== "") {
                pesoLimpio = parseInt(pesoRaw);
                if (isNaN(pesoLimpio)) pesoLimpio = 0;
            }

            if (pesoLimpio < 0) {
                throw new Error(`Fila ${excelRowNumber} (Ruta "${lastRouteName}"): Contiene un peso negativo (${pesoLimpio}).`);
            }

            if (typeof latRaw === 'string') latRaw = latRaw.replace(',', '.');
            if (typeof lonRaw === 'string') lonRaw = lonRaw.replace(',', '.');

            const lat = parseFloat(latRaw);
            const lon = parseFloat(lonRaw);
            const pName = row["Nombre Parada"] || `Parada ${i + 1}`;

            if (isNaN(lat) || isNaN(lon)) continue;

            const coordKey = `${lat.toFixed(5)},${lon.toFixed(5)}`;

            if (globalCoordWeights.hasOwnProperty(coordKey)) {
                const pesoPrevio = globalCoordWeights[coordKey];
                
                if (pesoPrevio !== pesoLimpio) {
                    throw new Error(
                        `Error de Consistencia en Fila ${excelRowNumber}: \n` +
                        `La parada en (${lat}, ${lon}) ya fue definida previamente con peso "${pesoPrevio}", ` +
                        `pero en la ruta "${lastRouteName}" intenta asignarse con peso "${pesoLimpio}". \n` +
                        `Una ubicación física debe tener siempre el mismo peso.`
                    );
                }
            } else {
                globalCoordWeights[coordKey] = pesoLimpio;
            }

            const check = await validateCountry(lat, lon);

            if (!check.isValid) {
                throw new Error(`Fila ${excelRowNumber} (Ruta "${lastRouteName}"): Coordenada inválida. ${check.country}`);
            }

            const rName = lastRouteName;
            if (!routesMap[rName]) {
              routesMap[rName] = {
                id: Date.now() + Math.random(),
                name: rName,
                color: "#EAC024",
                stops: [],
                type: 'candidate'
              };
            }
            
            // 2. VALIDAR DUPLICADOS EN LA MISMA RUTA
            const duplicateInRoute = routesMap[rName].stops.some(s => 
                Math.abs(s.lat - lat) < 0.0001 && Math.abs(s.lon - lon) < 0.0001
            );
            
            if (duplicateInRoute) {
                throw new Error(`Fila ${excelRowNumber} (Ruta "${lastRouteName}"): Contiene una parada duplicada en las coordenadas (${lat}, ${lon}). No se permite repetir la misma parada física dentro de una misma ruta.`);
            }

            bounds.hasValidCoords = true;
            if (lat < bounds.minLat) bounds.minLat = lat;
            if (lat > bounds.maxLat) bounds.maxLat = lat;
            if (lon < bounds.minLon) bounds.minLon = lon;
            if (lon > bounds.maxLon) bounds.maxLon = lon;

            routesMap[rName].stops.push({
              id: Date.now() + Math.random(),
              lat, lon, population: pesoLimpio, name: pName, isShared: false
            });
        }

        const newRoutes = Object.values(routesMap);
        if (newRoutes.length === 0) throw new Error("No se encontraron rutas válidas en el archivo.");
        for (const route of newRoutes) {
                    if (route.stops.length < 2) {
                        throw new Error(`Ruta Incompleta: La ruta "${route.name}" solo tiene ${route.stops.length} parada(s) válida(s). Cada ruta en el Excel debe contener al menos 2 puntos distintos para trazar un recorrido.`);
                    }
                }
        resolve({ newRoutes, bounds });

      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = (error) => reject(new Error("No se pudo leer el archivo Excel."));
    reader.readAsBinaryString(file);
  });
};